import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { MERCHANT_ADMIN_PRODUCTS_CREATE_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { FetchPriceListsResponse, PriceListQuery, fetchPriceLists } from "../../priceList/priceListApi"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { FetchProductsResponse, MerchantProductsQuery, fetchProducts } from "../productsApi"
import { PriceListDropdownSelector } from "../../priceList/components/PriceListDropdownSelector"
import { AllProductsTab } from "./AllProductsTab"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"

const selectPriceLists = (data: FetchPriceListsResponse) => data?.priceLists || []
const selectHasNoPriceListProducts = (data: FetchProductsResponse) => data?.productGroups?.length > 0

export function Products() {
    const navigate = useNavigate()
    const { merchantId } = useMerchant()
    const [selectedPriceListId, setSelectedPriceListId] = useState<number | "NONE" | null>(null)

    const { data: priceLists = [], isLoading } = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectPriceLists,
    })

    const { data: hasNoPriceListProducts = false } = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: { merchantId, priceListId: "NONE" as const },
        select: selectHasNoPriceListProducts,
    })

    const hasPriceLists = priceLists.length > 0

    useEffect(() => {
        if (selectedPriceListId !== null || priceLists.length === 0) return
        const firstActive = priceLists.find((pl) => pl.isActive)
        setSelectedPriceListId(firstActive?.id ?? priceLists[0].id)
    }, [priceLists, selectedPriceListId])

    return (
        <>
            <LocalizedStrict id="products-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Products"
                    page={Page.MERCHANT_PRODUCTS}
                    rightElement={
                        hasPriceLists ? (
                            <LocalizedStrict id="products-create-product-button">
                                <StandardButton
                                    onClick={() => {
                                        navigate(MERCHANT_ADMIN_PRODUCTS_CREATE_ROUTE)
                                    }}
                                >
                                    Create product
                                </StandardButton>
                            </LocalizedStrict>
                        ) : undefined
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            {selectedPriceListId !== null && (
                <AllProductsTab
                    priceListId={selectedPriceListId}
                    toolbarLeftContent={
                        !isLoading && (
                            <StandardTableFilter variant="left">
                                <PriceListDropdownSelector
                                    priceLists={priceLists}
                                    isLoading={isLoading}
                                    selectedPriceListId={selectedPriceListId}
                                    onPriceListChange={setSelectedPriceListId}
                                    showNoPriceListOption={hasNoPriceListProducts}
                                />
                            </StandardTableFilter>
                        )
                    }
                />
            )}
        </>
    )
}
